import { maxOne, sumOne } from 'rise-ui/utils/array.js';
import { getFitText } from 'rise-ui/utils/dom.js';
import { isSet, oneSet } from 'rise-ui/utils/nullish.js';
import { parseDrawOptions } from 'rise-ui/utils/canvas/graph-draw.js';

function parseGraphTextOptions(options) {
  options = parseDrawOptions(options);
  Object.assign(options, {
    gap: options.gap || 0,
    align: options.align || 'center',
    subtextRatio: oneSet(options.subtextRatio, 0.8),
    fontMultiplier: oneSet(options.fontMultiplier, 1),
  });
  return options;
}

const px = px => `${px}px`;

/**
 * @name DrawGraphTextOptionsArg
 * @extends DrawOptions
 * @property {number} [width] Width of text section
 * @property {number} [height] Height of text section
 * @property {boolean} [fitText] Whether to fit text to explicit width/height
 * @property {number} [fontMultiplier] Multiplier for all font sizes
 * @property {Array.<string|[string, number]>} [texts] Texts used instead of text or subtext
 * @property {string} [text] Main text
 * @property {number} [textSize] Main text font size
 * @property {string} [subtext] Secondary text displayed with main text
 * @property {number} [subtextSize] Secondary text font size
 * @property {number} [subtextRatio] Ratio of subtext height to text height (default: 0.8)
 * @property {number} [gap] Pixel width gap between text and subtext
 * @property {'start'|'center'|'end'} [align] Align text (default: center)
 */

/**
 * Get calculated text sizes
 * @param {DrawGraphTextOptionsArg} options
 * @return {{ options: DrawGraphTextOptionsArg, texts: { width: number, height: number, fontSize: number }[], width: number, height: number }}
 */
export function getGraphTextSizes(options) {
  options = parseGraphTextOptions(options);

  let { width, height } = options;
  const isWidthSet = isSet(width);
  const isHeightSet = isSet(height);

  const { paddingBottom, paddingLeft, paddingTop, paddingRight } = options;

  const { text, subtext, subtextRatio, fontSize, fontFamily, fontMultiplier } = options;
  let { texts, textSize, subtextSize } = options;
  texts = texts || [];

  if (subtext) {
    const headSize = oneSet(textSize, fontSize);
    subtextSize = oneSet(subtextSize, headSize * oneSet(subtextRatio, 1));
    texts = [ [ subtext, subtextSize ], ...texts ];
  }
  if (text) {
    texts = [ [ text, textSize ], ...texts ];
  }

  const fontOptions = {
    font: fontFamily,
    lineHeight: options.lineHeight,
    balanceText: options.balanceText,
    context: options.context,
  };

  let textsSize = { width: 0, height: 0 };
  texts = texts.map(text => {
    let size;
    text = [].concat(text || []);
    [ text, size ] = text;

    if (!text || isSet(size) && !size) {
      return { width: 0, height: 0, fontSize: 0 };
    }

    size = oneSet(size, fontSize) * fontMultiplier;
    const opts = {
      ...fontOptions,
      width: isWidthSet ? width - paddingLeft - paddingRight : undefined,
    };
    opts.fontSize = size;

    const fitText = getFitText(text, opts);

    textsSize.width = maxOne(fitText.width, textsSize.width);
    textsSize.height = sumOne(fitText.height, textsSize.height);

    return {
      width: fitText.width,
      height: fitText.height,
      fontSize: size,
      text,
      lines: fitText.lines,
      sizes: fitText.sizes,
    };
  });

  const { fitText, gap } = options;
  if (fitText && (isWidthSet || isHeightSet)) {
    let wSpace = !isWidthSet ? Infinity
      : width - paddingLeft - paddingRight;
    let hSpace = !isHeightSet ? Infinity
      : height - paddingTop - paddingBottom
        - gap * Math.max(0, texts.length - 1);
    const usedWidth = textsSize.width / wSpace;
    const usedHeight = textsSize.height / hSpace;
    const isTooWide = usedWidth > 1;
    const isTooHigh = usedHeight > 1;

    if (isTooWide || isTooHigh) {
      const fitRatio = 1 / (usedWidth > usedHeight ? usedWidth : usedHeight);
      textsSize = { width: 0, height: 0 };
      texts = texts.map(({ text, fontSize }) => {
        if (!text || !fontSize) {
          return { width: 0, height: 0, fontSize: 0 };
        }

        const opts = { ...fontOptions };
        fontSize *= fitRatio;
        opts.fontSize = px(fontSize);

        const fitText = getFitText(text, opts);

        textsSize.width = maxOne(fitText.width, textsSize.width);
        textsSize.height = sumOne(fitText.height, textsSize.height);

        return {
          width: fitText.width,
          height: fitText.height,
          fontSize,
          text,
          lines: fitText.lines,
          sizes: fitText.sizes,
        };
      });
    }
  }

  const isEmpty = !(textsSize.width && textsSize.height);
  if (!isWidthSet) {
    width = isEmpty ? 0 : textsSize.width + paddingLeft + paddingRight;
  }
  if (!isHeightSet) {
    height = isEmpty ? 0 : textsSize.height + paddingTop + paddingBottom
      + gap * Math.max(0, texts.length - 1);
  }
  return { options, texts, width, height };
}

/**
 * Get calculated overall text section size
 * @param {DrawGraphTextOptionsArg} options
 * @return {{ width: number, height: number }}
 */
export function getGraphTextSize(options) {
  const { width, height } = getGraphTextSizes(options);
  return { width, height };
}

/**
 * Draw graph texts
 * @param {CanvasRenderingContext2D} context
 * @param {DrawGraphTextOptionsArg} options
 */
export function drawGraphText(context, options) {
  const fitText = getGraphTextSizes({ context, ...options });
  const { texts, width } = fitText;
  options = fitText.options;
  const { gap, fontFamily, fontColor, align, paddingLeft, paddingTop, paddingRight } = options;

  const x = align => align === 'left' ? paddingLeft
    : align === 'right' ? width - paddingRight
    : (width - paddingRight - paddingLeft) / 2 + paddingLeft;
  let y = paddingTop;

  context.textBaseline = 'top';
  context.fillStyle = fontColor || '#000';
  context.textAlign = align;

  for (let iText = 0, nTexts = texts.length; iText < nTexts; ++iText) {
    const { lines, sizes, fontSize, height } = texts[iText];
    context.font = `${fontSize}px ${fontFamily}`;
    for (let iLine = 0, nLines = lines.length; iLine < nLines; ++iLine) {
      context.fillText(lines[iLine], x(align), y);
      y += sizes[iLine].height + gap;
    }
  }
}
